// components/dashboard/findMeATenant/FindMeATenantFlow.jsx

import React, { useState } from "react";
import {
  Dialog, DialogContent, Stepper, Step, StepLabel, Box,
  Typography, Button, CircularProgress, Alert, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ListingCriteriaStep from "./ListingCriteriaStep";
import PropertyIntakeForm from "./PropertyIntakeForm";
import FindMeATenantPaymentStep from "./FindMeATenantPaymentStep";
import { createFindMeATenantOrder } from "../../../services/FindMeATenantOrderService";

// Step indices
const STEP_CRITERIA = 0;
const STEP_PROPERTY = 1;
const STEP_PAYMENT  = 2;
const STEP_DONE     = 3;

const STEPS = ["Listing Criteria", "Property Info", "Payment", "Order Created"];

/**
 * FindMeATenantFlow
 *
 * Runs AFTER the blanket agreement is signed (FindMeATenantButton handles
 * that gate). Full flow:
 *   Listing Criteria → Property Intake → Payment (save card) → Order Created
 *
 * Props:
 *   open      {bool}
 *   onClose   {func}
 *   uid       {string}
 *   email     {string}
 *   ownerName {string}
 */
const FindMeATenantFlow = ({ open, onClose, uid, email, ownerName }) => {
  const [activeStep, setActiveStep] = useState(STEP_CRITERIA);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const reset = () => {
    setActiveStep(STEP_CRITERIA);
    setPropertyInfo(null);
    setCreatingOrder(false);
    setOrderError(null);
    setOrderId(null);
  };

  const handleClose = () => {
    if (creatingOrder) return;
    reset();
    onClose?.();
  };

  const handlePropertySubmit = (data) => {
    setPropertyInfo(data);
    setActiveStep(STEP_PAYMENT);
  };

  const handlePaymentSaved = async ({ customerId }) => {
    setCreatingOrder(true);
    setOrderError(null);
    try {
      const { orderId: newId } = await createFindMeATenantOrder({
        uid, email, ownerName,
        stripeCustomerId: customerId,
        propertyInfo,
      });
      setOrderId(newId);
      setActiveStep(STEP_DONE);
    } catch (err) {
      console.error("[FindMeATenantFlow] order creation failed:", err);
      setOrderError(err.message || "Failed to create your order. Please try again.");
    } finally {
      setCreatingOrder(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1, pr: 1 }}>
        <IconButton onClick={handleClose} disabled={creatingOrder} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 0, pb: 4, px: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === STEP_CRITERIA && (
          <ListingCriteriaStep
            onAccept={() => setActiveStep(STEP_PROPERTY)}
          />
        )}

        {activeStep === STEP_PROPERTY && (
          <PropertyIntakeForm
            onSubmit={handlePropertySubmit}
            onBack={() => setActiveStep(STEP_CRITERIA)}
          />
        )}

        {activeStep === STEP_PAYMENT && (
          <>
            {creatingOrder && (
              <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Creating your order…
                </Typography>
              </Box>
            )}
            {orderError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {orderError}
              </Alert>
            )}
            <FindMeATenantPaymentStep
              email={email}
              onSaved={handlePaymentSaved}
              onBack={() => setActiveStep(STEP_PROPERTY)}
            />
          </>
        )}

        {activeStep === STEP_DONE && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1.5}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
            <Typography variant="h6" fontWeight={700}>Order Placed!</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Order #{orderId} has been created and our team has been notified.
              A showing agent will begin working on your property shortly.
              Your card will be charged <strong>$395</strong> in 30 days.
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{ mt: 2, textTransform: "none", borderRadius: 2, px: 3 }}
            >
              Done
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FindMeATenantFlow;
